import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IPerson } from '@app/@shared/model/person.model';
import { IProperty } from '@shared/model/property.model';
import { FacebookService } from '@app/@shared/services/api/facebook.service';
import { GoogleOauthService } from '@app/@shared/services/api/google-oauth.service';
import { PersonService } from '@app/@shared/services/api/person.service';
import { PropertiesService } from '@shared/services/api/properties.service';
import { SellService } from '@shared/services/api/sell.service';
import { UsersService } from '@shared/services/api/users-account.service';
import { CaptacionRegistroService } from '@shared/services/api/captacion-registro.service';
import { InitGoogleMapsService } from '@app/@shared/services/api/init-google-maps.service';
import { CredentialsService } from '@shared/services/web/credentials.service';
import { AuthenticationService } from '@app/auth/authentication.service';
import { environment } from '@env/environment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { ToastrService } from '@app/@shared/services/web/toastr.service';
import { DatalayerService } from '@app/@shared/services/web/datalayer.service';
import { RegistroSocialComponent } from '@app/security/hidden-register/registro-social/registro-social.component';
import { CookieService } from 'ngx-cookie-service';
declare var google: any;

@Component({
  selector: 'app-sign-in-modal',
  templateUrl: './sign-in-modal.component.html',
  styleUrls: ['./sign-in-modal.component.scss'],
})
/**
 * Modal de registro / inicio. Tres flujos según "¿Qué quieres hacer en Inviertis?":
 *
 * • Común:  step 0 (Welcome) → step 1 (Registro)
 * • Invertir: step 2 (Sobre ti) → 3 (Encuesta 1) → 4 (Encuesta 2) → 5 (Preferencias) → 6 (Éxito)
 * • Vender:  step 2 (Sobre ti, tipo Vender) → 7 (Localización) → 8 (Propiedad) → 6 (Éxito)
 */
export class SignInModalComponent implements OnInit, AfterViewInit, OnDestroy {

  // ─── Flujo común (todos los usuarios) ─────────────────────────────────────
  public googleRequest: any;
  public currentLang = '';
  public registerForm!: FormGroup;
  public currentStep = 0;
  public cookieHsUtk = '';
  public userTypes: Array<any> = [];
  public userTypeSelected: any = null;
  public newPerson?: IPerson | any;
  public showPassword = false;
  /** true cuando step 1 se precargó desde Google/Facebook (cuenta nueva); oculta el password y evita el chequeo de email. */
  public isSocialSignup = false;
  public socialProvider: 'google' | 'facebook' | null = null;
  public socialToken: string | null = null;
  /** true cuando el email de step 1 (flujo manual) ya tiene cuenta; muestra el aviso de login en vez de avanzar. */
  public emailAlreadyExists = false;
  public checkingEmail = false;
  /** true mientras está abierto el LoginModalComponent anidado; oculta este modal sin cerrarlo (ver openLoginModal()). */
  public switchingToLogin = false;

  // ─── Flujo Invertir: encuesta y preferencias (steps 3, 4, 5) ───────────────
  // Survey answers
  //public surveyObjective: string = '';
  //public surveyRisk: string = '';
  //public surveyInvolvement: string = '';
  //public surveyDecision: string = '';
  //public surveyPhrase: string = '';
  public surveyObjective: { value: string; code: string; } = { value: '', code: '' };
  public surveyRisk: { value: string; code: string; } = { value: '', code: '' };
  public surveyInvolvement: { value: string; code: string; } = { value: '', code: '' };
  public surveyDecision: { value: string; code: string; } = { value: '', code: '' };
  public surveyPhrase: { value: string; code: string; } = { value: '', code: '' };

  // Preferences
  public prefZones: string = '';
  /** Valor de `FORM_DATA_STEP_PREFERENCES.modules[0].options[].value` para la zona elegida. */
  public prefSelectedZone: string = '';
  public prefMinTicket: string = '';
  public prefMaxTicket: string = '';
  public prefAssetTypes: string[] = [];
  public prefPropertiesCount: string = '';
  public prefProblematic: boolean = false;

  // ─── Flujo Vender: contacto vendedor y propiedad (steps 7, 8) ─────────────
  public isSeller = false;
  public myContactForm?: FormGroup;
  public localizationForm?: FormGroup;
  public propertyForm?: FormGroup;
  public salerCountries: Array<any> = [
    { id_country: 1, description: 'España', description_en: 'Spain' },
  ];
  public propertyTypes: any[] = [];
  public roadTypes: any[] = [];
  public locations: any[] = [];
  public provinces: any[] = [];
  public salerLanguage = '';
  public salerLanguageUrl = 'es';
  public submited1Saler = false;
  public submited2Saler = false;
  /** true mientras register() está en curso (Invertir u Vender); muestra spinner en el botón Finalizar/Enviar. */
  public isRegistering = false;
  public salerUser: any;
  public isNewUserSaler: boolean | null = null;
  public validateEmailSaler = false;
  private stepHistory: number[] = [];
  private destroy$ = new Subject<void>(); 
  private readonly USER_TYPE_INVESTOR = 1;
  private readonly USER_TYPE_SELLER = 2;

  private get passwordValue(): string {
    return this.registerForm.get('password')?.value || '';
  }

  get hasMinLength(): boolean {
    //return (this.registerForm.get('password')?.value?.length || 0) >= 8;
    return this.passwordValue.length >= 8;
  }
  get hasUpperCase(): boolean {
    //return /[A-Z]/.test(this.registerForm.get('password')?.value || '');
    return /[A-Z]/.test(this.passwordValue);
  }
  get hasLowerCase(): boolean {
    //return /[a-z]/.test(this.registerForm.get('password')?.value || '');
    return /[a-z]/.test(this.passwordValue);
  }
  get hasNumber(): boolean {
    //return /\d/.test(this.registerForm.get('password')?.value || '');
    return /\d/.test(this.passwordValue);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // ─── Flujo común: construcción y formulario de registro ──────────────────
  constructor(
    public dialogRef: MatDialogRef<SignInModalComponent>,
    public translateService: TranslateService,
    public dialog: MatDialog,
    private facebookService: FacebookService,
    private googleOauth: GoogleOauthService,
    private registerService: PersonService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(PLATFORM_ID) private platformId: any,
    private datalayerService: DatalayerService,
    private propertiesService: PropertiesService,
    private sellService: SellService,
    private usersService: UsersService,
    private captacionRegistroService: CaptacionRegistroService,
    private googleMapsService: InitGoogleMapsService,
    private cookieService: CookieService,
    private credentialsService: CredentialsService,
    private authenticationService: AuthenticationService,
  ) {
    this.createForm();
    /*this.registerService
      .getUserTypes()
      .toPromise()
      .then((data) => {
        this.userTypes = data.body;
      });*/
    firstValueFrom(this.registerService.getUserTypes()).then((data) => {
        this.userTypes = data.body;
    });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$))
      .subscribe(() => {this.updateSalerLanguage();});
    /*this.translateService.onLangChange.subscribe(() => {
      this.updateSalerLanguage();
    });*/
    this.updateSalerLanguage();
  }


  /** Flujo Vender: actualiza sufijo de idioma (es/en) para textos y URLs. */
  private updateSalerLanguage(): void {
    const lang = this.translateService.getCurrentLang();
    this.salerLanguage = lang === 'en' ? '_en' : '';
    this.salerLanguageUrl = lang === 'en' ? 'en' : 'es';
  }

  /** Formulario principal de registro (steps 1 y 2). */
  private createForm(): void {
    this.registerForm = this.formBuilder.group({
      first_name: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[A-ZñÑa-zÁ-ÿ0-9\\s]+$'),
          Validators.maxLength(30),
        ]),
      ],
      last_name: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern('^[A-ZñÑa-zÁ-ÿ0-9\\s]+$'),
          Validators.maxLength(30),
        ]),
      ],
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(5),
          Validators.maxLength(50),
        ]),
      ],
      code_country: [null, [Validators.required]],
      factory: [false],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(
            '^[+]?([1-9][0-9]*(?:[\\.][0-9]*)?|0*\\.0*[1-9][0-9]*)(?:[eE][+-][0-9]+)?$',
          ),
          Validators.minLength(8),
        ],
      ],
      /** Check box el opcional si no lleva Validators.requiredTrue */
      //legal: [false, Validators.requiredTrue],
      legal: [false], 
      cif: [''],
      company_name: [''],
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(
            '^(?=[^A-Z]*[A-Z])(?=[^a-z]*[a-z])(?=\\D*\\d)[A-ZñÑa-z\\d!¡?¿$%@#£€*&^<>()+-_{}]{8,}$',
          ),
          Validators.minLength(8),
          Validators.maxLength(20),
        ]),
      ],
    });
  }

  /** Inicialización de Google OAuth para registro social (step 0). */
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.googleRequest = google.accounts.oauth2.initTokenClient({
        client_id: environment.GOOGLE_APP_ID,
        scope:
          'https://www.googleapis.com/auth/userinfo.profile ' +
          'https://www.googleapis.com/auth/userinfo.email',
        prompt: 'consent',
        callback: (tokenResponse: any) => {
          this.googleOauth
            .getUserInfo(tokenResponse.access_token)
            .subscribe((userResponse: any) => {
              this.signUpWithGoogle(userResponse, tokenResponse.access_token);
            });
        },
      });
    }
  }

  ngOnInit(): void {
    /*this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.currentLang = event.lang;
    });
    */
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {this.currentLang = event.lang;});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /**
   * Google desde step 0: intenta iniciar sesión directamente (cuenta ya existente).
   * Solo si no hay cuenta para ese email (401) cae al registro precargado (step 1).
   */
  signUpWithGoogle(user: any, token: string) {
    if (!user) return;

    const general_data = {
      cookieHsUtk: this.cookieHsUtk,
      page: isPlatformBrowser(this.platformId) ? window.location.href : '',
      name: 'Login for Google',
    };

    this.authenticationService.loginGoogle({ token, general_data }).subscribe({
      next: () => {
        this.toastr.success('a Inviertis', 'Bienvenido');
        this.datalayerService.push('form_submitted', {
          form_id: 'form_login',
          form_name: 'Formulario Login',
          form_location: 'modal',
          page_language: this.translateService.getCurrentLang(),
          page_location: isPlatformBrowser(this.platformId)
            ? window.location.href
            : '',
          page_path: isPlatformBrowser(this.platformId)
            ? window.location.pathname
            : '',
          method: 'google',
        });
        this.dialogRef.close();
      },
      error: (error: any) => {
        if (error.status !== 401) {
          this.toastr.error(error.error?.message, 'Error al iniciar sesión con Google');
          return;
        }

        // No existe cuenta para este email todavía: precargar y continuar el registro (sin password).
        this.isSocialSignup = true;
        this.socialProvider = 'google';
        this.socialToken = token;
        this.registerForm.get('password')?.clearValidators();
        this.registerForm.get('password')?.setValue('');
        this.registerForm.get('password')?.updateValueAndValidity();

        this.goToStep(1);
        this.registerForm.controls['first_name'].setValue(user.given_name);
        this.registerForm.controls['last_name'].setValue(user.family_name);
        this.registerForm.controls['email'].setValue(user.email);

        this.datalayerService.push('form_started', {
          form_id: 'form_register_modal',
          form_name: 'Formulario Registro',
          page_language: this.translateService.getCurrentLang(),
          page_location: isPlatformBrowser(this.platformId)
            ? window.location.href
            : '',
          page_path: isPlatformBrowser(this.platformId)
            ? window.location.pathname
            : '',
          method: 'google',
        });
      },
    });
  }

  /**
   * Facebook desde step 0: mismo patrón que Google — intenta iniciar sesión
   * directamente; solo si no hay cuenta (401) cae al registro precargado (step 1).
   */
  signUpWithFB() {
    this.facebookService.loginWithFacebook().then(
      (user) => {
        this.authenticationService.loginFb(user).subscribe({
          next: () => {
            this.toastr.success('a Inviertis', 'Bienvenido');
            this.datalayerService.push('form_submitted', {
              form_id: 'form_login',
              form_name: 'Formulario Login',
              form_location: 'modal',
              page_language: this.translateService.getCurrentLang(),
              page_location: isPlatformBrowser(this.platformId)
                ? window.location.href
                : '',
              page_path: isPlatformBrowser(this.platformId)
                ? window.location.pathname
                : '',
              method: 'facebook',
            });
            this.dialogRef.close();
          },
          error: (error: any) => {
            if (error.status !== 401) {
              this.toastr.error(error.error?.message, 'Error al iniciar sesión con Facebook');
              return;
            }

            // No existe cuenta para este email todavía: precargar y continuar el registro (sin password).
            this.isSocialSignup = true;
            this.socialProvider = 'facebook';
            this.socialToken = user.authToken;
            this.registerForm.get('password')?.clearValidators();
            this.registerForm.get('password')?.setValue('');
            this.registerForm.get('password')?.updateValueAndValidity();

            this.goToStep(1);
            this.registerForm.controls['first_name'].setValue(user.firstName);
            this.registerForm.controls['last_name'].setValue(user.lastName);
            this.registerForm.controls['email'].setValue(user.email);

            this.datalayerService.push('form_started', {
              form_id: 'form_register_modal',
              form_name: 'Formulario Registro',
              form_location: 'modal',
              page_language: this.translateService.getCurrentLang(),
              page_location: isPlatformBrowser(this.platformId)
                ? window.location.href
                : '',
              page_path: isPlatformBrowser(this.platformId)
                ? window.location.pathname
                : '',
              method: 'facebook',
            });
          },
        });
      },
      () => {
        this.toastr.error('Inicio de sesión fallido', '');
      },
    );
  }

  /** Step 2 (Sobre ti): selección Invertir / Vender / Otros; si Vender, inicializa flujo vendedor. */
  selectType(type: any) {
    this.userTypeSelected = type.id_user_type;
    const desc: string = (
      type.description ??
      type.description_en ??
      ''
    ).toLowerCase();
    this.isSeller = desc.includes('vend') || desc.includes('sell');
    if (this.isSeller) {
      this.myContactForm = this.formBuilder.group({
        first_name: [
          this.registerForm.get('first_name')?.value ?? null,
          [Validators.required],
        ],
        last_name: [
          this.registerForm.get('last_name')?.value ?? null,
          [Validators.required],
        ],
        email: [
          this.registerForm.get('email')?.value ?? null,
          [Validators.required, Validators.email],
        ],
        phone_number: [
          this.registerForm.get('phone')?.value ?? null,
          [Validators.required],
        ],
        code_country: [
          this.registerForm.get('code_country')?.value ?? null,
          [],
        ],
        terms: [false, [Validators.requiredTrue]],
        receive_info: [false, []],
        contact_type: [[], [Validators.required]],
      });
      this.createSalerForms();
      this.getPropertyTypes();
      this.getProvinces();
      this.getTypesStreet();
      this.localizationForm?.controls.province?.valueChanges.subscribe(
        (value) => {
          this.getLocationByProvince(value);
        },
      );
    }
  }

  /** Flujo Vender: crea formularios de localización y propiedad (steps 7, 8). */
  createSalerForms(): void {
    this.propertyForm = this.formBuilder.group({
      propertyType: [null, [Validators.required]],
      ocupation_state: [false, []],
      rent_price: [null, []],
      desc_state: [null, []],
    });
    this.localizationForm = this.formBuilder.group({
      roadType: [null, [Validators.required]],
      street: [null, [Validators.required]],
      no_street: [null, [Validators.required]],
      floor: [null, []],
      door: [null, []],
      ladder: [null, []],
      location: [null, [Validators.required]],
      postal_code: [null, [Validators.required]],
      province: [null, [Validators.required]],
      country: [1, [Validators.required]],
      latitude: [null, []],
      longitude: [null, []],
    });
  }

  /** Flujo Vender: tipos de propiedad para step 8. */
  getPropertyTypes(): void {
    this.propertiesService.getPropertyType().subscribe((data) => {
      this.propertyTypes = data;
    });
  }

  /** Flujo Vender: tipos de vía para step 7 (localización). */
  getTypesStreet(): void {
    this.propertiesService.getTypesStreet().subscribe((data) => {
      this.roadTypes = data;
    });
  }

  /** Flujo Vender: provincias para step 7. */
  getProvinces(): void {
    this.sellService.getProvinces().subscribe((data) => {
      this.provinces = data;
    });
  }

  /** Flujo Vender: localidades por provincia (step 7). */
  getLocationByProvince(idProvince: any): void {
    this.sellService.getLocationsByProvince(idProvince).subscribe((data) => {
      this.locations = data;
      this.localizationForm?.get('location')?.setValue(null);
    });
  }

  /** Flujo Vender: toggle ocupación y validación de rent_price (step 8). */
  salerOcupationToggle(): void {
    if (!this.propertyForm) return;
    if (this.propertyForm.controls.ocupation_state.value) {
      this.propertyForm.controls.rent_price.setValidators([
        Validators.required,
      ]);
    } else {
      this.propertyForm.controls.rent_price.clearValidators();
      this.propertyForm.get('rent_price')?.setValue(null);
      this.propertyForm.controls.rent_price.updateValueAndValidity();
    }
  }

  /** Flujo Vender: geocodificación de la dirección (step 8). */
  getCoordsProperty(): Promise<any> {
    if (!this.localizationForm) {
      return Promise.reject({ status: false });
    }
    const provinceSelect = this.provinces.find(
      (prov: any) =>
        prov.id_province ===
        Number(this.localizationForm.get('province')?.value),
    )?.description;
    const locationSelect = this.locations.find(
      (loc: any) =>
        loc.id_location ===
        Number(this.localizationForm.get('location')?.value),
    )?.description;
    const streetSelect = this.localizationForm.get('street')?.value;
    const no_streetSelect = this.localizationForm.get('no_street')?.value;
    const address = `${streetSelect}, ${no_streetSelect}, ${locationSelect}, ${provinceSelect}`;
    return new Promise((resolve, reject) => {
      this.googleMapsService.getGeocode(address).subscribe((data: any) => {
        if (data.status === 'OK') {
          this.localizationForm
            .get('longitude')
            ?.setValue(data.results[0].geometry.location.lng);
          this.localizationForm
            .get('latitude')
            ?.setValue(data.results[0].geometry.location.lat);
          resolve({ status: true });
        } else {
          reject({ status: false });
        }
      });
    });
  }

  /** Flujo Vender: crea o obtiene usuario para el formulario de contacto (step 7/8). */
  registerSalerUser(): void {
    if (!this.myContactForm) return;
    const email = this.myContactForm.get('email')?.value;
    this.propertiesService.getEmail(email).subscribe({
      next: (resp) => {
        if (resp?.id_user) {
          this.salerUser = resp;
          if (this.isNewUserSaler === null) this.isNewUserSaler = false;
        } else {
          const codeCountry = this.myContactForm.get('code_country')?.value;
          const user: any = {
            email,
            receiveNotifications: true,
            receiveNotificationsContent:
              this.myContactForm.get('receive_info')?.value,
            receiveNotificationsProcesses: true,
            receiveNotificationsCommercial:
              this.myContactForm.get('receive_info')?.value,
            cookieHsUtk: this.cookieService.get('hubspotutk'),
            person: {
              first_name: this.myContactForm.get('first_name')?.value,
              last_name: this.myContactForm.get('last_name')?.value,
              phone: {
                number: this.myContactForm.get('phone_number')?.value,
                code_country: codeCountry?.phone_code ?? '34',
              },
            },
            authorities: { authority: { id_authority: 3 } },
            user_contact: {
              contact_email: (
                this.myContactForm.value.contact_type || []
              ).includes(1),
              contact_phone: (
                this.myContactForm.value.contact_type || []
              ).includes(2),
              contact_sms: (
                this.myContactForm.value.contact_type || []
              ).includes(3),
            },
            typeHS: 'WIZZARD',
          };
          this.usersService.create(user).subscribe({
            next: (data) => {
              this.salerUser = data;
              if (this.isNewUserSaler === null) this.isNewUserSaler = true;
              this.validateEmailSaler = true;
            },
            error: (err) => {
              this.toastr.error(err.error.message, 'Error en el servidor');
            },
          });
        }
      },
      error: () => {
        this.toastr.error(
          'Ocurrió un error al registrar usuario',
          'Error en el servidor',
        );
      },
    });
  }

  /** Flujo Vender: validación/registro de email tras envío (usuarios existentes). */
  checkEmailSaler(): void {
    if (!this.myContactForm) return;
    const email = this.myContactForm.get('email')?.value;
    this.propertiesService.getEmail(email).subscribe({
      next: (resp) => {
        if (Object.keys(resp || {}).length === 0) {
          const newPassword = this.captacionRegistroService.randomPassword(10);
          this.propertiesService
            .registerEmail(
              {
                email: this.captacionRegistroService.email,
                receiveNotifications: false,
                password: newPassword,
                repeat_password: newPassword,
                cookieHsUtk: this.cookieService.get('hubspotutk'),
              },
              true,
            )
            .subscribe(() => {
              this.validateEmailSaler = true;
              this.onEmailValidationRequired();
            });
        } else {
          if (!resp.verified) {
            const emailUser = {
              email: this.captacionRegistroService.email,
              complete: !resp.hasPassword,
            };
            this.propertiesService.SendEmail(emailUser).subscribe(() => {
              this.validateEmailSaler = true;
              this.onEmailValidationRequired();
            });
          } else {
            this.dialog.open(RegistroSocialComponent, {
              data: {
                email: resp.email,
                redirectTo: `./${this.salerLanguageUrl}/tablero-de-usuario/editar-propiedad/`,
              },
              panelClass: 'icon-logo-modal',
            });
          }
        }
      },
    });
  }

  /** Flujo común: valida step 1 (registro) para habilitar siguiente paso. */
  firstStepValidate(): boolean {
    const firstName = this.registerForm.controls['first_name'].valid;
    const lastName = this.registerForm.controls['last_name'].valid;
    const email = this.registerForm.controls['email'].valid;
    const pass = this.registerForm.controls['password'].valid;
    return firstName && lastName && email && pass;
  }

  /** Step 2 (Sobre ti): validadores CIF/empresa cuando es persona jurídica. */
  onRegisterTypeChange() {
    if (this.registerForm.controls['factory'].value) {
      this.registerForm
        .get('cif')
        ?.setValidators([
          Validators.maxLength(9),
          Validators.pattern('^[A-Za-z0-9_-]*$'),
        ]);
      this.registerForm
        .get('company_name')
        ?.setValidators([Validators.required, Validators.maxLength(50)]);
    } else {
      this.registerForm.get('cif')?.setValidators(null);
      this.registerForm.get('company_name')?.setValidators(null);
    }
    this.registerForm.get('cif')?.updateValueAndValidity();
    this.registerForm.get('company_name')?.updateValueAndValidity();
  }

  /** Flujo común: enlace aviso legal y cierre del modal. */
  goToLegal() {
    this.router.navigate([`/${this.translateService.currentLang}/aviso-legal`]);
    this.dialogRef.close();
  }

  /** Flujo común: abre modal de login y cierra este modal. */
  openLoginModal() {
    // No cerrar este modal hasta que el de login termine (éxito o cancelado):
    // si se cerraba antes, quien esté escuchando el cierre de este diálogo
    // (p. ej. el flujo de Gran Inversor) lo interpretaba como "no logueado"
    // y redirigía a otra página antes de que el login realmente terminara.
    this.switchingToLogin = true;
    const loginRef = this.dialog.open(LoginModalComponent, {
      maxWidth: '95vw',
      panelClass: 'icon-logo-modal',
    });
    loginRef.afterClosed().subscribe(() => {
      this.dialogRef.close();
    });
  }

  /** Flujo Invertir: registro final tras steps 1→2→3→4→5; actualmente avanza a step 6 (éxito). */
  register() {
    this.isRegistering = true;
    // TODO: Clon_Form_Signin_Plataforma
    const formSignin = {
      cif: this.registerForm.get('cif')?.value,
      name: this.registerForm.get('company_name')?.value,
      firstname: this.registerForm.get('first_name')?.value,
      lastname: this.registerForm.get('last_name')?.value,
      phone: this.registerForm.get('phone')?.value
        ? {
          number: this.registerForm.get('phone')?.value,
          code_country: this.registerForm.get('code_country')?.value
            ? this.registerForm.get('code_country')?.value.phone_code
            : '34',
        }
        : null,
      email: this.registerForm.get('email')?.value,
      vender_invertir_alquilar: this.userTypes.find(
        (value) => value.id_user_type === this.userTypeSelected,
      ).description,
      desea_notificaciones: this.registerForm.controls['legal'].value
        ? 'Si'
        : 'No',
      has_invertido_antes_: 'No',
      form_id: '5f6efa9a-f4e2-4a24-a5bf-2d7aa96afd16',
      form_name: 'Clon_Form_Signin_Plataforma',
      form_location: 'modal',
      page_language: this.translateService.getCurrentLang(),
      page_location: isPlatformBrowser(this.platformId)
        ? window.location.href
        : '',
      cookieHsUtk: this.cookieHsUtk,
    };

    let formWizzard: Record<string, unknown> | null = null;
    let formPerfilInversor: Record<string, unknown> | null = null;
    let formReference: Record<string, unknown> | null = null;

    //TODO: Clon_Form_Wizzard_Deal + perfil inversor + referencias (solo tipo inversor)
    if (this.userTypeSelected === this.USER_TYPE_SELLER) {
      formWizzard = {
        address: {
          id_address: '',
          no_catastral: '',
          street: this.localizationForm.get('street')?.value,
          streetType: {
            id_street_type: this.localizationForm.get('roadType')?.value,
          },
          postal_code: this.localizationForm.get('postal_code')?.value,
          no_street: this.localizationForm.get('no_street')?.value,
          floor: this.localizationForm.get('floor')?.value,
          door: this.localizationForm.get('door')?.value,
          ladder: this.localizationForm.get('ladder')?.value,
          location: {
            id_location: this.localizationForm.get('location')?.value,
          },
          more_info: '',
          latitude: this.localizationForm.get('latitude')?.value,
          longitude: this.localizationForm.get('longitude')?.value,
        },
        propertyType: {
          id_prop_type: this.propertyForm.get('propertyType')?.value,
        },
        propertyRentals: [
          {
            id_prop_rental: '',
            ocupationState: this.propertyForm.get('ocupation_state')?.value,
            rented_situation: this.propertyForm.get('desc_state')?.value,
            rent_price: this.propertyForm.get('rent_price')?.value,
          },
        ],
        form_id: '9fc82333-45fc-47ed-9158-cfd508e9e028',
        form_name: 'Clon_Form_Wizzard_Deal',
        form_location: 'modal',
        page_language: this.translateService.getCurrentLang(),
        page_location: isPlatformBrowser(this.platformId)
          ? window.location.href
          : '',
        cookieHsUtk: this.cookieHsUtk,
      };
    }
    if (this.userTypeSelected ===  this.USER_TYPE_INVESTOR) {
      //TODO: form_perfil_inversor
      formPerfilInversor = {
        cual_es_tu_objetivo_principal_al_invertir_en_un_inmueble: this.surveyObjective.value,
        code_answ1: this.surveyObjective.code,
        que_nivel_de_riesgo_estas_dispuesto_a_asumir: this.surveyRisk.value,
        code_answ2: this.surveyRisk.code,
        como_quieres_implicarte_en_la_inversion: this.surveyInvolvement.value,
        code_answ3: this.surveyInvolvement.code,
        que_te_da_mas_tranquilidad_al_tomar_una_decision: this.surveyDecision.value,
        code_answ4: this.surveyDecision.code,
        que_frase_describe_mejor_como_quieres_que_funcione_esta_inversion_para_ti: this.surveyPhrase.value,
        code_answ5: this.surveyPhrase.code,
        form_id: '6b0c153e-8550-43fe-84b0-88811cb8e9e9',
        form_name: 'Perfil Inversor',
        form_location: 'modal',
        page_language: this.translateService.getCurrentLang(),
        page_location: isPlatformBrowser(this.platformId)
          ? window.location.href
          : '',
        cookieHsUtk: this.cookieHsUtk,
      };

      //TODO: form reference
      formReference = {
        prefZonesChange: this.prefZones,
        prefSelectedZoneChange: this.prefSelectedZone,
        prefMinTicketChange: this.prefMinTicket,
        prefMaxTicketChange: this.prefMaxTicket,
        prefPropertiesCountChange: this.prefPropertiesCount,
        prefProblematicChange: this.prefProblematic,
        toggleAssetType: this.prefAssetTypes,
      };
    }

    // this.isLoading = true;

    this.newPerson = {
      form_id: 'form_register_modal',
      form_name: 'Formulario Registro',
      form_location: 'modal',
      page_language: this.translateService.getCurrentLang(),
      page_location: isPlatformBrowser(this.platformId)
        ? window.location.href
        : '',
      page_path: isPlatformBrowser(this.platformId)
        ? window.location.pathname
        : '',
      user_type: this.userTypeSelected,
      first_name: this.registerForm.get('first_name')?.value,
      last_name: this.registerForm.get('last_name')?.value,
      cif: this.registerForm.get('cif')?.value,
      company_name: this.registerForm.get('company_name')?.value,
      phone: this.registerForm.get('phone')?.value
        ? {
          number: this.registerForm.get('phone')?.value,
          code_country: this.registerForm.get('code_country')?.value
            ? this.registerForm.get('code_country')?.value.phone_code
            : '34',
        }
        : null,
      user: {
        email: this.registerForm.get('email')?.value,
        password: this.registerForm.get('password')?.value,
        receiveNotifications: true,
        receiveNotificationsContent: false,
        receiveNotificationsProcesses: true,
        receiveNotificationsCommercial: false,
        // experienced_investor: this.esInversor ? this.tipoDeInversor : null,
        userTypes: { id_user_type: this.userTypeSelected },
      },
      personType: {
        id_person_type: !this.registerForm.controls['factory'].value ? 1 : 2,
      },
      formSignin,
      formWizzard,
      formPerfilInversor,
      formReference,
    };

    if (this.registerForm.controls['legal'].value) {
      if (this.newPerson.user) {
        this.newPerson.user.receiveNotificationsContent = true;
        this.newPerson.user.receiveNotificationsCommercial = true;
      }
    }

    this.newPerson.cookieHsUtk = this.cookieHsUtk;

    if (this.isSocialSignup) {
      this.newPerson.social_provider = this.socialProvider;
      this.newPerson.access_token = this.socialToken;
    }

    this.registerService.register(this.newPerson).subscribe(
      (x) => {
        this.datalayerService.push('form_submitted', {
          form_id: 'form_register_modal',
          form_name: 'Formulario Registro',
          form_location: 'modal',
          page_language: this.translateService.getCurrentLang(),
          page_location: isPlatformBrowser(this.platformId)
            ? window.location.href
            : '',
          page_path: isPlatformBrowser(this.platformId)
            ? window.location.pathname
            : '',
          user_type: this.userTypeSelected,
        });

        this.isRegistering = false;

        if (this.isSocialSignup) {
          // Cuenta social recién creada (ya verificada en backend): loguea de fondo,
          // pero siempre muestra el step de éxito (con botón Aceptar) pase lo que
          // pase con el login automático, para no dejar la pantalla sin feedback.
          const login$ =
            this.socialProvider === 'facebook'
              ? this.authenticationService.loginFb({ authToken: this.socialToken })
              : this.authenticationService.loginGoogle(this.socialToken);
          login$.subscribe({
            next: () => this.goToStep(6),
            error: () => this.goToStep(6),
          });
          return;
        }

        this.goToStep(6);
        const iconLogoModal = document.querySelector(
          '.icon-logo-modal',
        ) as HTMLElement;
        if (iconLogoModal) {
          iconLogoModal.style.setProperty('--bg-icon', 'none');
        }

        /*setTimeout(() => {
          this.dialogRef.close();
        }, 5000);*/
      },
      (error: any) => {
        this.isRegistering = false;
        this.toastr.error(error.error.message, 'Registro fallido');
      },
    );
  }

  /**
   * Step 1 (Registro manual): antes de avanzar a step 2, chequea si el email ya
   * tiene cuenta (mismo endpoint que usa el sub-flujo Vender). Si es un signup
   * social ya sabemos que no existe (se verificó al intentar loguear), así que
   * se salta el chequeo.
   */
  onRegisterStepContinue(): void {
    this.emailAlreadyExists = false;

    if (this.isSocialSignup) {
      this.goToStep(2);
      return;
    }

    const email = this.registerForm.get('email')?.value;
    this.checkingEmail = true;
    this.propertiesService.getEmail(email).subscribe({
      next: (resp) => {
        this.checkingEmail = false;
        if (resp?.id_user) {
          this.emailAlreadyExists = true;
        } else {
          this.goToStep(2);
        }
      },
      error: () => {
        this.checkingEmail = false;
        this.goToStep(2);
      },
    });
  }

  /** Navegación entre steps; mantiene historial para goBack(). */
  goToStep(step: number): void {
    this.stepHistory.push(this.currentStep);
    this.currentStep = step;

    if ([3, 4].includes(step)) {
      this.datalayerService.push('form_step_completed', {
        form_id: '6b0c153e-8550-43fe-84b0-88811cb8e9e9',
        form_name: 'Perfil Inversor',
        form_location: 'modal',
        page_language: this.translateService.getCurrentLang(),
        page_location: isPlatformBrowser(this.platformId)
          ? window.location.href
          : '',
      });
    } else if ([7, 8].includes(step)) {
      this.datalayerService.push('form_Signin_Plataforma', {
        form_id: '5f6efa9a-f4e2-4a24-a5bf-2d7aa96afd16',
        form_name: 'Clon_Form_Signin_Plataforma',
        form_location: 'modal',
        page_language: this.translateService.getCurrentLang(),
        page_location: isPlatformBrowser(this.platformId)
          ? window.location.href
          : '',
      });
    }
  }

  /** Vuelve al step anterior usando el historial. */
  goBack(): void {
    if (this.stepHistory.length === 0) return;
    this.currentStep = this.stepHistory.pop()!;
  }

  gocloseModal(): void {
    this.resetStateAfterSuccessStep();
    this.dialogRef.close();
  }

  /**
   * Step 6 (éxito): limpieza general — formularios, encuesta, preferencias,
   * flujo vendedor, historial de atrás y payload de registro (p. ej. contraseña).
   */
  private resetStateAfterSuccessStep(): void {
    this.stepHistory = [];
    this.teardownCompleteSalerFlow();
    this.userTypeSelected = null;
    this.isSeller = false;
    this.clearInvestorSurveyAndPrefs();
    this.createForm();
    this.showPassword = false;
    this.newPerson = null;
    this.patchRegisterStep2OnlyFields();
    this.resetPropertyFormAfterBack();
    this.isSocialSignup = false;
    this.socialProvider = null;
    this.socialToken = null;
    this.emailAlreadyExists = false;
  }

  private clearPreferencesState(): void {
    this.prefZones = '';
    this.prefSelectedZone = '';
    this.prefMinTicket = '';
    this.prefMaxTicket = '';
    this.prefAssetTypes = [];
    this.prefPropertiesCount = '';
    this.prefProblematic = false;
  }

  private clearInvestorSurveyAndPrefs(): void {
    //this.surveyObjective = '';
    //this.surveyRisk = '';
    //this.surveyInvolvement = '';
    //this.surveyDecision = '';
    //this.surveyPhrase = '';
    this.surveyObjective = { value: '', code: '' };
    this.surveyRisk = { value: '', code: '' };
    this.surveyInvolvement = { value: '', code: '' };
    this.surveyDecision = { value: '', code: '' };
    this.surveyPhrase = { value: '', code: '' };
    this.clearPreferencesState();
  }

  private teardownCompleteSalerFlow(): void {
    this.myContactForm = null;
    this.localizationForm = null;
    this.propertyForm = null;
    this.locations = [];
    this.submited1Saler = false;
    this.submited2Saler = false;
    this.isRegistering = false;
    this.salerUser = null;
    this.validateEmailSaler = false;
    this.isNewUserSaler = null;
  }

  private resetPropertyFormAfterBack(): void {
    if (!this.propertyForm) return;
    this.propertyForm.reset({
      propertyType: null,
      ocupation_state: false,
      rent_price: null,
      desc_state: null,
    });
    this.propertyForm.get('rent_price')?.clearValidators();
    this.propertyForm.get('rent_price')?.updateValueAndValidity();
  }

  /** Campos añadidos en «Sobre ti» (step 2); se limpian al volver al registro (step 1). */
  private patchRegisterStep2OnlyFields(): void {
    this.registerForm.patchValue({
      code_country: null,
      phone: '',
      legal: false,
      factory: false,
      cif: '',
      company_name: '',
    });
    this.onRegisterTypeChange();
  }

  /** Flujo Vender: cierre del modal tras completar contacto vendedor. */
  onSalerContactComplete(): void {
    this.toastr.success('¡Formulario enviado correctamente!', '');
    this.dialogRef.close();
  }

  /** Flujo Vender: mensaje cuando se requiere validar email. */
  onEmailValidationRequired(): void {
    this.toastr.info(
      this.translateService.instant(
        'components.modalSuccesfull.needValidateEmail',
      ),
      '',
    );
  }

  /** Flujo Invertir (step 5 Preferencias): selección de tipos de activo. */
  toggleAssetType(type: string): void {
    const idx = this.prefAssetTypes.indexOf(type);
    if (idx >= 0) this.prefAssetTypes.splice(idx, 1);
    else this.prefAssetTypes.push(type);
  }
}
